/**
 * @file Pinia store for authentication.
 * @description
 * Single source of truth for user authentication state. Holds the current user, access token,
 * loading and error flags. Exposes high-level actions (`login`, `logout`, `register`, `fetchUserProfile`,
 * `ensurePublicSessionToken`, `handleOauthCallback`) that call `authService`/`apiService` and update state accordingly.
 * UI components should consume this store to check authentication (`isAuthenticated`, `isSuperAdmin`, `isManager`) and to trigger auth flows.
 */
import { defineStore } from "pinia";
import { ref, computed, Ref } from "vue";
import {
  authService,
  UserCredentials as AuthCredentials,
} from "../services/auth";
import { apiService, getPublicSessionToken } from "../services/api.service";
import { tGlobal } from "@/i18n";
import { UserRole } from "../types/roles";
import { posthogService } from "@/services/posthog.service";
import { gtmService } from "@/services/gtm.service";
import { resetAllStores } from "@/plugins/piniaReset";

// Interface pour l'utilisateur alignée avec ce que USERS.ME pourrait retourner
// et compatible avec AuthServiceUser.
export interface User {
  id?: string;
  email?: string;
  username?: string;
  role?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  organization_id?: string;
  organization_slug?: string;
  organization_is_setup_complete?: boolean;
  organization_is_demo?: boolean;
  organization?: {
    id?: string;
    name?: string;
    slug?: string;
    is_setup_complete?: boolean;
  };
  fullname?: string;
  phone?: string;
  timezone?: string;
  /** L'utilisateur a demandé à ne pas être inclus dans les statistiques. */
  ignore_tracking?: boolean;
  /** Champ réellement renvoyé par le backend. L'ancien `avatar` n'était
   *  jamais renseigné à la connexion : l'avatar disparaissait au rechargement. */
  avatar_url?: string;
  notes?: string;
  active?: boolean;
  created_at?: string;
}

// Interface pour les données d'inscription
export interface RegisterData {
  email: string;
  username: string;
  password: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  plan?: string | (string | null)[];
  organization?: {
    name: string;
  };
  organization_name?: string;
  role?: UserRole;
}


const SOMETHING_WENT_WRONG_KEY = 'auth.messages.something_went_wrong';

export const useAuthStore = defineStore("auth", () => {
  const user: Ref<User | null> = ref(null);
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);
  const publicSessionToken: Ref<string | null> = ref(
    sessionStorage.getItem("public_session_token") || null,
  );

  // Consider the user authenticated when we have a user profile loaded.
  // This supports both header-based JWT and cookie-based sessions (OAuth).
  const isAuthenticated = computed(() => !!user.value);
  const isSuperAdmin = computed(
    () => user.value?.role === UserRole.SUPER_ADMIN,
  );
  const isManager = computed(() => {
    const role = user.value?.role;
    return (
      role === UserRole.ORGANIZATION_ADMIN ||
      role === UserRole.MANAGER ||
      role === "manager"
    );
  });
  const isOrgAdmin = computed(
    () => user.value?.role === UserRole.ORGANIZATION_ADMIN,
  );


  const clearUser = () => {
    user.value = null;
  };

  const logAuthWarning = (context: string, error: unknown): void => {
    if (import.meta.env.DEV) {
      console.warn(`[authStore] ${context}`, error);
    }
  };

  const initializeUser = async (): Promise<void> => {
    isLoading.value = true;
    try {
      // Utilisation exclusive des cookies pour l'authentification
      await fetchUserProfile();
    } catch (error: unknown) {
      logAuthWarning("initializeUser failed", error);
      clearUser();
    } finally {
      isLoading.value = false;
    }
  };

  const fetchUserProfile = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      const fetchedUser = await authService.fetchCurrentUser();
      user.value = fetchedUser;
      if (fetchedUser) {
        // Préférence « Ignorer le suivi » : elle était enregistrée mais lue par
        // personne — l'utilisateur qui demandait à ne pas être suivi l'était
        // quand même. Elle s'ajoute au consentement cookies, elle ne le
        // remplace pas : refuser ici suffit à couper le suivi.
        if (fetchedUser.ignore_tracking) {
          posthogService.shutdown();
        } else {
          // Identify the user in PostHog (no-op if not consented or not initialized)
          posthogService.identify({
            id: fetchedUser.id ?? '',
            email: fetchedUser.email,
            role: fetchedUser.role,
            orgSlug: fetchedUser.organization_slug,
          });
        }
      }
    } catch (err: unknown) {
      error.value = tGlobal("errors.server_error");
      // Keep browsing as guest when profile cannot be fetched
      clearUser();
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const login = async (credentials: AuthCredentials): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;
    try {
      const loginSuccess = await authService.login(credentials);
      if (loginSuccess) {
        // Authentification réussie via cookies, récupérer le profil utilisateur
        await fetchUserProfile();
        gtmService.trackLogin();
        return true;
      }
      throw new Error(tGlobal("auth.login.fail"));
    } catch (loginErr: unknown) {
      logAuthWarning("login failed", loginErr);
      error.value = tGlobal("auth.login.fail");
      clearUser();
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error: unknown) {
      logAuthWarning("logout failed", error);
    } finally {
      clearUser();
      // Purge tous les stores de domaine : sans cela, les données du tenant
      // précédent (lacs, réservations, tarification…) restaient visibles après
      // un logout→login dans le même onglet (poste partagé).
      resetAllStores(["auth"]);
      // Reset PostHog identity on logout (no-op if not initialized)
      posthogService.reset();
    }
  };

  const register = async (
    userData: RegisterData,
  ): Promise<{ success: boolean; needsSetup: boolean }> => {
    isLoading.value = true;
    error.value = null;
    try {
      await authService.register({
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
      });
      const loginSuccess = await login({
        username: userData.email,
        password: userData.password,
      });
      if (loginSuccess && user.value) {
        gtmService.trackSignUp();
        return {
          success: true,
          needsSetup: !user.value.organization_is_setup_complete,
        };
      }
      return { success: false, needsSetup: false };
    } catch (err: unknown) {
      // Toast explicite pour cas connus
      try {
        const { useNotificationStore } = await import("./notificationStore");
        const notificationStore = useNotificationStore();
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        if (
          typeof detail === "string" &&
          detail.toLowerCase().includes("already exists")
        ) {
          notificationStore.error(
            tGlobal("auth.register.already_have_account"),
          );
        } else {
          notificationStore.error(
            tGlobal(SOMETHING_WENT_WRONG_KEY),
          );
        }
      } catch (error: unknown) {
        logAuthWarning("register notification handling failed", error);
      }
      error.value = tGlobal(SOMETHING_WENT_WRONG_KEY);
      clearUser();
      return { success: false, needsSetup: false };
    } finally {
      isLoading.value = false;
    }
  };

  const handleOauthCallback = async () => {
    // OAuth pose automatiquement un cookie, récupérer le profil utilisateur
    try {
      await fetchUserProfile();
      // `fetchCurrentUser` avale volontairement le 401 (cas normal : visiteur
      // anonyme au chargement de l'app) et résout avec `null` au lieu de
      // lever. Après un VRAI retour OAuth, un `user` toujours nul signifie que
      // le cookie n'a jamais été posé : sans ce contrôle explicite, le `catch`
      // ci-dessous n'était jamais atteint, et la page affichait « connecté »
      // avec un utilisateur nul.
      if (!user.value) {
        throw new Error("OAuth callback: no authenticated user after fetchUserProfile");
      }
      gtmService.trackLogin('oauth');
      error.value = null;
    } catch (oauthErr: unknown) {
      logAuthWarning("handleOauthCallback failed", oauthErr);
      error.value = tGlobal("errors.server_error");
      await logout();
      throw new Error(error.value ?? undefined);
    }
  };

  const ensurePublicSessionToken = async (): Promise<void> => {
    if (publicSessionToken.value) {
      // Ne pas changer le header global; les appels publics utiliseront leur propre instance API
      return;
    }
    try {
      const token = await getPublicSessionToken();
      if (token) {
        publicSessionToken.value = token;
        sessionStorage.setItem("public_session_token", token);
        // Ne pas définir le header Authorization global avec un jeton public
      }
    } catch (error: unknown) {
      logAuthWarning("ensurePublicSessionToken failed", error);
    }
  };

  const finalizeStripeConnect = async (code: string): Promise<void> => {
    // Appel API pour finaliser la configuration Stripe
    await apiService.post('/auth/stripe/finalize', { code });
    // Rafraîchir le profil utilisateur pour obtenir les nouvelles informations Stripe
    await fetchUserProfile();
  };

  // Passerelles fines vers authService pour les pages d'authentification
  // (canon 2 couches) : les pages gardent leur propre état de formulaire.
  const redirectToGoogleAuth = (redirectUrl?: string): Promise<void> =>
    authService.redirectToGoogleAuth(redirectUrl);

  const forgotPassword = (email: string): Promise<void> =>
    authService.forgotPassword(email);

  const resetPassword = (password: string, token: string): Promise<void> =>
    authService.resetPassword(password, token);

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isSuperAdmin,
    isManager,
    isOrgAdmin,
    publicSessionToken,
    initializeUser,
    login,
    logout,
    clearUser,
    fetchUserProfile,
    register,
    handleOauthCallback,
    ensurePublicSessionToken,
    finalizeStripeConnect,
    redirectToGoogleAuth,
    forgotPassword,
    resetPassword,
  };
});
